import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import {
  Agent,
  LayerDetail,
  LayerEditFormState,
  Style
} from '../../../shared/models/agent';

export type LayerModalMode = 'add' | 'edit';

/** Unified payload the modal emits on save. Parent branches on mode to
 *  decide whether to call the create or update endpoint. `agentId === null`
 *  is allowed in 'add' mode ("bind later") and rejected by the component's
 *  own validation in 'edit' mode. */
export interface LayerModalSavePayload {
  name: string;
  agentId: string | null;
  updateFrequency: number;
}

/** Read-only catalog of styles the backend will seed on a new layer. Kept
 *  in sync with _NEW_LAYER_STYLES in the Python FleetService — if either
 *  side changes the catalog, update both. */
const DEFAULT_LAYER_STYLES: ReadonlyArray<{ name: string; fillColor: string }> = [
  { name: 'default',      fillColor: '#000000' },
  { name: 'start',        fillColor: '#4CAE4C' },
  { name: 'stop',         fillColor: '#AC2925' },
  { name: 'acceleration', fillColor: '#B6D7A8' },
  { name: 'deceleration', fillColor: '#EA9999' },
  { name: 'geofence',     fillColor: '#46b8da' },
  { name: 'route',        fillColor: '#95d6ea' },
  { name: 'speeding',     fillColor: '#eea236' },
  { name: 'online',       fillColor: '#000000' },
  { name: 'offline',      fillColor: '#5A5A5A' },
  { name: 'active',       fillColor: '#000000' },
  { name: 'parked',       fillColor: '#000000' },
  { name: 'heartbeat',    fillColor: '#000000' }
];

@Component({
  selector: 'layer-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './layer-modal.component.html'
})
export class LayerModalComponent implements OnChanges {
  icons = Icons;
  readonly defaultStyles = DEFAULT_LAYER_STYLES;

  @Input() mode: LayerModalMode = 'add';
  @Input() open = false;
  @Input() saving = false;
  /** Disables the tracker dropdown while the parent fetches assignable
   *  agents. The same flag also doubles as the modal-level "still loading"
   *  signal in edit mode (we wait on the layer-detail fetch as well). */
  @Input() loading = false;
  /** Trackers eligible for the dropdown. In add mode this is the unbound
   *  list; in edit mode it includes the layer's currently-bound agent so
   *  the existing selection stays valid. */
  @Input() assignableAgents: Agent[] = [];

  // --- 'edit' mode only ---
  /** Source-of-truth for hydrating the form in edit mode. */
  @Input() initialLayer: LayerDetail | null = null;
  /** Styles owned by this layer — drives the editable Styles list. */
  @Input() styles: Style[] = [];
  @Input() loadingStyles = false;

  @Output() save = new EventEmitter<LayerModalSavePayload>();
  @Output() closeModal = new EventEmitter<void>();
  /** Edit-mode only — opens the Add-Style flow from the styles list. */
  @Output() addStyle = new EventEmitter<void>();
  /** Edit-mode only — opens the Edit-Style flow for a row. */
  @Output() editStyle = new EventEmitter<Style>();
  /** Edit-mode only — deletes a style row (parent shows confirmation). */
  @Output() deleteStyle = new EventEmitter<Style>();

  form = signal<LayerEditFormState | null>(null);
  /** Fields the user has interacted with (blurred at least once). We only
   *  show validation errors for touched fields so they don't flash on open. */
  touched = signal<Set<string>>(new Set<string>());

  /** False when a required field is missing — drives the submit-button
   *  disabled state. Name is always required; agentId is also required
   *  in edit mode (v2's btnSaveLiveLayer guard). */
  readonly canSubmit = computed<boolean>(() => {
    const f = this.form();
    if (!f) return false;
    if (!f.name.trim()) return false;
    if (this.mode === 'edit' && !f.agentId) return false;
    return true;
  });

  markTouched(field: string): void {
    this.touched.update(s => {
      if (s.has(field)) return s;
      const next = new Set(s);
      next.add(field);
      return next;
    });
  }

  showError(field: string, invalid: boolean): boolean {
    return invalid && this.touched().has(field);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-hydrate the form whenever the modal opens, the mode flips, or
    // the bound layer detail changes. Each open is a fresh form.
    if (changes['open'] || changes['initialLayer'] || changes['mode']) {
      if (!this.open) {
        this.form.set(null);
        this.touched.set(new Set<string>());
        return;
      }
      // Fresh open = clean slate; no fields touched yet.
      this.touched.set(new Set<string>());
      if (this.mode === 'edit' && this.initialLayer) {
        const l = this.initialLayer;
        this.form.set({
          layerId: l.id,
          name: l.name ?? '',
          agentId: l.agentId ?? '',
          updateFrequency: l.updateFrequency ?? 5
        });
      } else if (this.mode === 'add') {
        this.form.set({
          layerId: 0,
          name: 'Not Configured',
          agentId: '',
          updateFrequency: 5
        });
      }
    }
  }

  updateField<K extends keyof LayerEditFormState>(
    key: K,
    value: LayerEditFormState[K]
  ): void {
    this.form.update(f => (f ? { ...f, [key]: value } : f));
  }

  onSubmit(): void {
    const f = this.form();
    if (!f) return;
    if (!f.name.trim()) return;
    // v2's btnSaveLiveLayer requires a tracker in edit mode; add mode
    // allows null so the user can bind a tracker later from the layer row.
    if (this.mode === 'edit' && !f.agentId) return;
    this.save.emit({
      name: f.name.trim(),
      agentId: f.agentId ? f.agentId : null,
      updateFrequency: f.updateFrequency
    });
  }

  onClose(): void {
    if (this.saving) return;
    this.closeModal.emit();
  }

  isDefaultStyle(s: Style): boolean {
    return (s.name || '').toLowerCase() === 'default';
  }
}
